import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as StorageActions from './storage.actions';
import { StorageService } from '../../services/storage.service';
import { NotificationService } from '../../services/notification.service';
import { StorageDeleteReason } from '../../../shared/enums/storage.enum';
import { TranslateService } from '@ngx-translate/core';

export const loadStorageItems$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.loadStorageItems),
      mergeMap(() =>
        storageService.getStorageItems().pipe(
          map((items) => {
            // Optional: success message for loading can be skipped or uncommented if desired
            // notification.success('Storage items loaded.');
            return StorageActions.loadStorageItemsSuccess({ items });
          }),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.loadFailed'));
            return of(StorageActions.loadStorageItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadStorageQuota$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.loadStorageQuota),
      switchMap(() =>
        storageService.getStorageQuota().pipe(
          map((quota) => {
            // Optional: success message
            // notification.success('Storage quota loaded.');
            return StorageActions.loadStorageQuotaSuccess({ quota });
          }),
          catchError(() => {
            notification.error(translate.instant('storage.notifications.quotaFailed'));
            return of(StorageActions.loadStorageQuotaFailure());
          })
        )
      )
    ),
  { functional: true }
);

export const addStorageItem$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.addStorageItem),
      mergeMap(({ item }) =>
        storageService.addStorageItem(item).pipe(
          map((created) => {
            notification.success(translate.instant('storage.notifications.addSuccess'));
            return StorageActions.addStorageItemSuccess({ item: created });
          }),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.addFailed'));
            return of(StorageActions.addStorageItemFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateStorageItem$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.updateStorageItem),
      mergeMap(({ id, item }) =>
        storageService.updateStorageItem(id, item).pipe(
          map((updated) => {
            notification.success(translate.instant('storage.notifications.updateSuccess'));
            return StorageActions.updateStorageItemSuccess({ item: updated });
          }),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.updateFailed'));
            return of(StorageActions.updateStorageItemFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteStorageItem$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.deleteStorageItem),
      mergeMap(({ id, reason }) =>
        storageService.deleteStorageItem(id, reason).pipe(
          map(() => StorageActions.deleteStorageItemSuccess({ id, reason })),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.deleteFailed'));
            return of(StorageActions.deleteStorageItemFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const showDeleteNotification$ = createEffect(
  (
    actions$ = inject(Actions),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.deleteStorageItemSuccess),
      tap(({ reason }) => {
        const msg = translate.instant(
          reason === StorageDeleteReason.Consumed
            ? 'storage.notifications.markedConsumed'
            : 'storage.notifications.markedThrownOut'
        );
        notification.success(msg);
      })
    ),
  { functional: true, dispatch: false }
);

export const refreshStorageQuotaOnAdd$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(StorageActions.addStorageItemSuccess),
      map(() => StorageActions.loadStorageQuota())
    ),
  { functional: true }
);

export const refreshStorageQuotaOnDelete$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(StorageActions.deleteStorageItemSuccess),
      map(() => StorageActions.loadStorageQuota())
    ),
  { functional: true }
);

export const bulkAddStorageItems$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.bulkAddStorageItems),
      mergeMap(({ items }) =>
        storageService.bulkAddStorageItems(items).pipe(
          map((newItems) => {
            notification.success(translate.instant('storage.notifications.bulkAddSuccess'));
            return StorageActions.bulkAddStorageItemsSuccess({ items: newItems });
          }),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.bulkAddFailed'));
            return of(StorageActions.bulkAddStorageItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const bulkUpdateStorageItems$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.bulkUpdateStorageItems),
      mergeMap(({ items }) =>
        storageService.bulkUpdateStorageItems(items).pipe(
          map((updatedItems) => {
            notification.success(translate.instant('storage.notifications.bulkUpdateSuccess'));
            return StorageActions.bulkUpdateStorageItemsSuccess({ items: updatedItems });
          }),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.bulkUpdateFailed'));
            return of(StorageActions.bulkUpdateStorageItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const bulkDeleteStorageItems$ = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(StorageActions.bulkDeleteStorageItems),
      mergeMap(({ ids }) =>
        storageService.bulkDeleteStorageItems(ids).pipe(
          map(() => {
            notification.success(translate.instant('storage.notifications.bulkDeleteSuccess'));
            return StorageActions.bulkDeleteStorageItemsSuccess({ ids });
          }),
          catchError((error) => {
            notification.error(translate.instant('storage.notifications.bulkDeleteFailed'));
            return of(StorageActions.bulkDeleteStorageItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);


export const StorageEffects = {
  loadStorageItems$,
  loadStorageQuota$,
  addStorageItem$,
  updateStorageItem$,
  deleteStorageItem$,
  showDeleteNotification$,
  refreshStorageQuotaOnAdd$,
  refreshStorageQuotaOnDelete$,
  bulkUpdateStorageItems$,
  bulkAddStorageItems$,
  bulkDeleteStorageItems$
};
